import {
  useEffect,
  useState
} from "react";

import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp
} from "../../access/firestore";

import { db } from "../../firebase";

import { useClinic } from "../../context/ClinicContext";
import { requestVexaConfirm } from "../../notifications/dialogService";

import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  GripVertical
} from "lucide-react";


/*
=========================================================
 HISTORIA CLÍNICA - CONFIGURACIÓN
=========================================================

 Esta configuración pertenece EXCLUSIVAMENTE al
 consultorio identificado por clinicId.

 La configuración se guarda en:

 clinics/{clinicId}/historiaClinicaConfig/config

 Las cuatro secciones principales son:

 1. General
 2. Dirección
 3. Clínico
 4. Extras

 IMPORTANTE:

 Este componente solamente administra la configuración.

 NO modifica directamente las historias clínicas
 de los pacientes.

 La Historia clínica del paciente utilizará esta
 configuración posteriormente.

=========================================================
*/


const HISTORIA_CLINICA_DEFAULT = {

  version: 2,

  secciones: [

    /*
    =====================================================
     GENERAL
    =====================================================
    */

    {
      id: "general",

      titulo: "General",

      preguntas: [

        {
          id: "nombre",
          texto: "Nombre",
          tipo: "texto",
          campo: "nombre",
          obligatorio: true,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "apellidos",
          texto: "Apellidos",
          tipo: "texto",
          campo: "apellidos",
          obligatorio: true,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "sexo",
          texto: "Sexo",
          tipo: "seleccion",
          campo: "sexo",
          opciones: [
            "Masculino",
            "Femenino",
            "Otro"
          ],
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "estado_civil",
          texto: "Estado civil",
          tipo: "seleccion",
          campo: "estadoCivil",
          opciones: [
            "Soltero(a)",
            "Casado(a)",
            "Unión libre",
            "Divorciado(a)",
            "Viudo(a)"
          ],
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "fecha_nacimiento",
          texto: "Nacimiento",
          tipo: "fecha",
          campo: "fechaNacimiento",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "edad",
          texto: "Edad",
          tipo: "numero",
          campo: "edad",
          soloLectura: true,
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "peso",
          texto: "Peso (kg)",
          tipo: "numero",
          campo: "peso",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "telefono",
          texto: "Teléfono de Casa",
          tipo: "telefono",
          campo: "telefono",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "whatsapp",
          texto: "WhatsApp",
          tipo: "telefono",
          campo: "whatsapp",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "correo",
          texto: "Correo",
          tipo: "texto",
          campo: "correo",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "curp",
          texto: "CURP",
          tipo: "texto",
          campo: "curp",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "rfc",
          texto: "RFC",
          tipo: "texto",
          campo: "rfc",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "ocupacion",
          texto: "Ocupación",
          tipo: "texto",
          campo: "ocupacion",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "nacionalidad",
          texto: "Nacionalidad",
          tipo: "texto",
          campo: "nacionalidad",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        }

      ]
    },


    /*
    =====================================================
     DIRECCIÓN
    =====================================================
    */

    {
      id: "direccion",

      titulo: "Dirección",

      preguntas: [

        {
          id: "calle",
          texto: "Calle",
          tipo: "texto",
          campo: "calle",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "numero_exterior",
          texto: "No. exterior",
          tipo: "texto",
          campo: "numeroExterior",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "numero_interior",
          texto: "No. interior",
          tipo: "texto",
          campo: "numeroInterior",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "cp",
          texto: "Código postal",
          tipo: "texto",
          campo: "cp",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "colonia",
          texto: "Colonia",
          tipo: "texto",
          campo: "colonia",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "ciudad",
          texto: "Ciudad",
          tipo: "texto",
          campo: "ciudad",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "estado",
          texto: "Estado",
          tipo: "texto",
          campo: "estado",
          soloLectura: true,
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "pais",
          texto: "País",
          tipo: "texto",
          campo: "pais",
          soloLectura: true,
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        }

      ]
    },


    /*
    =====================================================
     CLÍNICO
    =====================================================
    */

    {
      id: "clinico",

      titulo: "Clínico",

      preguntas: [

        {
          id: "motivo_consulta",
          texto: "Motivo de consulta",
          tipo: "texto_largo",
          campo: "motivoConsulta",
          obligatorio: true,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "padecimiento_actual",
          texto: "Padecimiento actual",
          tipo: "texto_largo",
          campo: "padecimientoActual",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "dolor",
          texto: "Descripción del dolor",
          tipo: "texto",
          campo: "dolor",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "escala_dolor",
          texto: "Escala del dolor",
          tipo: "numero",
          campo: "escalaDolor",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "estado_salud_general",
          texto: "Salud general",
          tipo: "seleccion",
          campo: "estadoSaludGeneral",
          opciones: [
            "Buena",
            "Regular",
            "Mala"
          ],
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "covid_reciente",
          texto: "¿COVID reciente?",
          tipo: "si_no",
          campo: "covidReciente",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "alergias",
          texto: "Alergias",
          tipo: "texto_largo",
          campo: "alergias",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: true,
          alertaTexto: "Alergias"
        },

        {
          id: "antecedentes_medicos",
          texto: "Antecedentes médicos",
          tipo: "texto_largo",
          campo: "antecedentesMedicos",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "medicamentos",
          texto: "Medicamentos actuales",
          tipo: "texto_largo",
          campo: "medicamentos",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "cirugias",
          texto: "¿Ha tenido cirugías?",
          tipo: "si_no",
          campo: "cirugias",
          obligatorio: false,
          permitirEspecificar: true,
          especificacionLabel: "Especifique las cirugías",
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "transfusiones",
          texto: "¿Ha recibido transfusiones sanguíneas?",
          tipo: "si_no",
          campo: "transfusiones",
          obligatorio: false,
          permitirEspecificar: true,
          especificacionLabel: "Especifique",
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "diabetes",
          texto: "¿Padece diabetes?",
          tipo: "si_no",
          campo: "diabetes",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: true,
          alertaTexto: "Paciente diabético"
        },

        {
          id: "hipertension",
          texto: "¿Padece hipertensión arterial?",
          tipo: "si_no",
          campo: "hipertension",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: true,
          alertaTexto: "Hipertensión arterial"
        },

        {
          id: "cardiopatias",
          texto: "¿Padece alguna cardiopatía?",
          tipo: "si_no",
          campo: "cardiopatias",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: true,
          alertaTexto: "Cardiopatías"
        },

        {
          id: "asma",
          texto: "¿Padece asma?",
          tipo: "si_no",
          campo: "asma",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: true,
          alertaTexto: "Asma"
        },

        {
          id: "embarazo",
          texto: "¿Está embarazada?",
          tipo: "si_no",
          campo: "embarazo",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: true,
          alertaTexto: "Embarazo"
        },

        {
          id: "epilepsia",
          texto: "¿Padece epilepsia?",
          tipo: "si_no",
          campo: "epilepsia",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: true,
          alertaTexto: "Epilepsia"
        },

        {
          id: "hepatitis",
          texto: "¿Padece o ha padecido hepatitis?",
          tipo: "si_no",
          campo: "hepatitis",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: true,
          alertaTexto: "Hepatitis"
        },

        {
          id: "cancer",
          texto: "¿Tiene o ha tenido cáncer?",
          tipo: "si_no",
          campo: "cancer",
          obligatorio: false,
          permitirEspecificar: true,
          especificacionLabel: "Especifique",
          generaAlerta: true,
          alertaTexto: "Antecedente de cáncer"
        },

        {
          id: "ets",
          texto: "¿Tiene o ha tenido una ETS?",
          tipo: "si_no",
          campo: "ets",
          obligatorio: false,
          permitirEspecificar: true,
          especificacionLabel: "Especifique",
          generaAlerta: true,
          alertaTexto: "Antecedente de ETS"
        },

        {
          id: "drogas",
          texto: "¿Consume drogas?",
          tipo: "si_no",
          campo: "drogas",
          obligatorio: false,
          permitirEspecificar: true,
          especificacionLabel: "Sustancia / frecuencia",
          generaAlerta: true,
          alertaTexto: "Consumo de drogas"
        },

        {
          id: "anticonceptivos",
          texto: "¿Utiliza anticonceptivos?",
          tipo: "si_no",
          campo: "anticonceptivos",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "osteoporosis",
          texto: "¿Padece osteoporosis?",
          tipo: "si_no",
          campo: "osteoporosis",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: true,
          alertaTexto: "Osteoporosis"
        },

        {
          id: "anticoagulantes",
          texto: "¿Utiliza anticoagulantes?",
          tipo: "si_no",
          campo: "anticoagulantes",
          obligatorio: false,
          permitirEspecificar: true,
          especificacionLabel: "Especifique cuál",
          generaAlerta: true,
          alertaTexto: "Uso de anticoagulantes"
        },

        {
          id: "cancer_detalle",
          texto: "Especifique cáncer",
          tipo: "texto",
          campo: "cancerDetalle",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "ets_detalle",
          texto: "Especifique ETS",
          tipo: "texto",
          campo: "etsDetalle",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "drogas_sustancia",
          texto: "Sustancia / frecuencia de consumo",
          tipo: "texto",
          campo: "drogasSustancia",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "otros_padecimientos",
          texto: "Otros padecimientos",
          tipo: "texto_largo",
          campo: "otrosPadecimientos",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: true,
          alertaTexto: "Otros padecimientos"
        },

        {
          id: "tabaco",
          texto: "¿Fuma?",
          tipo: "si_no",
          campo: "tabaco",
          obligatorio: false,
          permitirEspecificar: true,
          especificacionLabel: "Cantidad / frecuencia",
          generaAlerta: true,
          alertaTexto: "Paciente fumador"
        },

        {
          id: "alcohol",
          texto: "¿Consume alcohol?",
          tipo: "si_no",
          campo: "alcohol",
          obligatorio: false,
          permitirEspecificar: true,
          especificacionLabel: "Cantidad / frecuencia",
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "bruxismo",
          texto: "¿Presenta bruxismo?",
          tipo: "si_no",
          campo: "bruxismo",
          obligatorio: false,
          permitirEspecificar: true,
          especificacionLabel: "Especifique",
          generaAlerta: true,
          alertaTexto: "Bruxismo"
        },

        {
          id: "cepillados_dia",
          texto: "¿Cuántas veces al día se cepilla los dientes?",
          tipo: "numero",
          campo: "cepilladosDia",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "hilo_dental",
          texto: "¿Utiliza hilo dental?",
          tipo: "si_no",
          campo: "hiloDental",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "enjuague",
          texto: "¿Utiliza enjuague bucal?",
          tipo: "si_no",
          campo: "enjuague",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "sangrado_encias",
          texto: "¿Presenta sangrado de encías?",
          tipo: "si_no",
          campo: "sangradoEncias",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "gingivitis",
          texto: "¿Presenta gingivitis?",
          tipo: "si_no",
          campo: "gingivitis",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "enfermedad_periodontal",
          texto: "¿Presenta enfermedad periodontal?",
          tipo: "si_no",
          campo: "enfermedadPeriodontal",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: true,
          alertaTexto: "Enfermedad periodontal"
        },

        {
          id: "halitosis",
          texto: "¿Presenta halitosis?",
          tipo: "si_no",
          campo: "halitosis",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "movilidad_dental",
          texto: "¿Presenta movilidad dental?",
          tipo: "si_no",
          campo: "movilidadDental",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "dificultad_masticar",
          texto: "¿Presenta dificultad para masticar?",
          tipo: "si_no",
          campo: "dificultadMasticar",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        }

      ]
    },


    /*
    =====================================================
     EXTRAS
    =====================================================
    */

    {
      id: "extras",

      titulo: "Extras",

      preguntas: [

        {
          id: "seguro_medico",
          texto: "Seguro médico",
          tipo: "texto",
          campo: "seguroMedico",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "numero_poliza",
          texto: "Número de póliza",
          tipo: "texto",
          campo: "numeroPoliza",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "contacto_emergencia",
          texto: "Contacto de emergencia",
          tipo: "texto",
          campo: "contactoEmergencia",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "telefono_emergencia",
          texto: "Teléfono de emergencia",
          tipo: "telefono",
          campo: "telefonoEmergencia",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "parentesco_emergencia",
          texto: "Parentesco",
          tipo: "texto",
          campo: "parentescoEmergencia",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        },

        {
          id: "observaciones",
          texto: "Observaciones",
          tipo: "texto_largo",
          campo: "observaciones",
          obligatorio: false,
          permitirEspecificar: false,
          generaAlerta: false,
          alertaTexto: ""
        }

      ]
    }

  ]

};


/*
=========================================================
 NORMALIZAR CONFIGURACIÓN
=========================================================
*/

function normalizarConfiguracionHistoria(data) {
  const base = JSON.parse(
    JSON.stringify(HISTORIA_CLINICA_DEFAULT)
  );

  if (!data) {
    return base;
  }

  // ============================================================
  // MAPEO DE PREGUNTAS EXISTENTES → CAMPOS LEGACY DEL PACIENTE
  // ============================================================
  const camposLegacy = {
    // GENERAL
    nombre: "nombre",
    apellidos: "apellidos",
    sexo: "sexo",
    estado_civil: "estadoCivil",
    fecha_nacimiento: "fechaNacimiento",
    edad: "edad",
    peso: "peso",
    telefono: "telefono",
    whatsapp: "whatsapp",
    correo: "correo",
    curp: "curp",
    rfc: "rfc",
    ocupacion: "ocupacion",
    nacionalidad: "nacionalidad",

    // DIRECCIÓN
    calle: "calle",
    numero_exterior: "numeroExterior",
    numero_interior: "numeroInterior",
    cp: "cp",
    colonia: "colonia",
    ciudad: "ciudad",
    estado: "estado",
    pais: "pais",

    // CLÍNICO
    motivo_consulta: "motivoConsulta",
    padecimiento_actual: "padecimientoActual",
    dolor: "dolor",
    escala_dolor: "escalaDolor",
    estado_salud_general: "estadoSaludGeneral",
    covid_reciente: "covidReciente",
    alergias: "alergias",
    antecedentes_medicos: "antecedentesMedicos",
    medicamentos: "medicamentos",
    cirugias: "cirugias",
    transfusiones: "transfusiones",

    diabetes: "diabetes",
    hipertension: "hipertension",
    cardiopatias: "cardiopatias",
    asma: "asma",
    embarazo: "embarazo",
    epilepsia: "epilepsia",
    hepatitis: "hepatitis",
    cancer: "cancer",
    ets: "ets",
    drogas: "drogas",
    anticonceptivos: "anticonceptivos",
    osteoporosis: "osteoporosis",
    anticoagulantes: "anticoagulantes",

    cancer_detalle: "cancerDetalle",
    ets_detalle: "etsDetalle",
    drogas_sustancia: "drogasSustancia",
    otros_padecimientos: "otrosPadecimientos",

    tabaco: "tabaco",
    alcohol: "alcohol",
    bruxismo: "bruxismo",

    cepillados_dia: "cepilladosDia",
    hilo_dental: "hiloDental",
    enjuague: "enjuague",

    sangrado_encias: "sangradoEncias",
    gingivitis: "gingivitis",
    enfermedad_periodontal: "enfermedadPeriodontal",
    halitosis: "halitosis",
    movilidad_dental: "movilidadDental",
    dificultad_masticar: "dificultadMasticar",

    // EXTRAS
    seguro_medico: "seguroMedico",
    numero_poliza: "numeroPoliza",
    contacto_emergencia: "contactoEmergencia",
    telefono_emergencia: "telefonoEmergencia",
    parentesco_emergencia: "parentescoEmergencia",
    observaciones: "observaciones",
  };

  // ============================================================
  // FUNCIÓN PARA ASEGURAR QUE UNA PREGUNTA TENGA SU CAMPO
  // LEGACY CUANDO CORRESPONDA
  // ============================================================
  const completarPregunta = (pregunta) => {
    if (!pregunta) return pregunta;

    // Si ya tiene campo, respetarlo.
    if (pregunta.campo) {
      return pregunta;
    }

    // Intentar identificarla por ID.
    const campoPorId = camposLegacy[pregunta.id];

    if (campoPorId) {
      return {
        ...pregunta,
        campo: campoPorId,
      };
    }

    // Intentar identificarla por un nombre/clave equivalente.
    const texto = String(
      pregunta.texto ||
      pregunta.pregunta ||
      pregunta.label ||
      ""
    )
      .trim()
      .toLowerCase();

    const equivalenciasTexto = {
      "nombre": "nombre",
      "apellidos": "apellidos",
      "sexo": "sexo",
      "estado civil": "estadoCivil",
      "nacimiento": "fechaNacimiento",
      "fecha de nacimiento": "fechaNacimiento",
      "edad": "edad",
      "peso": "peso",
      "teléfono": "telefono",
      "telefono": "telefono",
      "teléfono de casa": "telefono",
      "telefono de casa": "telefono",
      "whatsapp": "whatsapp",
      "correo": "correo",
      "email": "correo",
      "curp": "curp",
      "rfc": "rfc",
      "ocupación": "ocupacion",
      "ocupacion": "ocupacion",
      "nacionalidad": "nacionalidad",

      "calle": "calle",
      "número exterior": "numeroExterior",
      "numero exterior": "numeroExterior",
      "número interior": "numeroInterior",
      "numero interior": "numeroInterior",
      "código postal": "cp",
      "codigo postal": "cp",
      "cp": "cp",
      "colonia": "colonia",
      "ciudad": "ciudad",
      "estado": "estado",
      "país": "pais",
      "pais": "pais",

      "motivo de consulta": "motivoConsulta",
      "motivo consulta": "motivoConsulta",
      "padecimiento actual": "padecimientoActual",
      "dolor": "dolor",
      "escala del dolor": "escalaDolor",
      "escala dolor": "escalaDolor",
      "estado de salud general": "estadoSaludGeneral",
      "estado salud general": "estadoSaludGeneral",
      "covid reciente": "covidReciente",
      "alergias": "alergias",
      "antecedentes médicos": "antecedentesMedicos",
      "antecedentes medicos": "antecedentesMedicos",
      "medicamentos": "medicamentos",
      "cirugías": "cirugias",
      "cirugias": "cirugias",
      "transfusiones": "transfusiones",

      "diabetes": "diabetes",
      "hipertensión": "hipertension",
      "hipertension": "hipertension",
      "cardiopatías": "cardiopatias",
      "cardiopatias": "cardiopatias",
      "asma": "asma",
      "embarazo": "embarazo",
      "epilepsia": "epilepsia",
      "hepatitis": "hepatitis",
      "cáncer": "cancer",
      "cancer": "cancer",
      "ets": "ets",
      "drogas": "drogas",
      "anticonceptivos": "anticonceptivos",
      "osteoporosis": "osteoporosis",
      "anticoagulantes": "anticoagulantes",

      "detalle cáncer": "cancerDetalle",
      "detalle cancer": "cancerDetalle",
      "detalle ets": "etsDetalle",
      "sustancia": "drogasSustancia",
      "sustancia de consumo": "drogasSustancia",
      "otros padecimientos": "otrosPadecimientos",

      "tabaco": "tabaco",
      "tabaquismo": "tabaco",
      "alcohol": "alcohol",
      "bruxismo": "bruxismo",

      "cepillados al día": "cepilladosDia",
      "cepillados al dia": "cepilladosDia",
      "cepillados por día": "cepilladosDia",
      "cepillados por dia": "cepilladosDia",
      "hilo dental": "hiloDental",
      "enjuague": "enjuague",

      "sangrado de encías": "sangradoEncias",
      "sangrado de encias": "sangradoEncias",
      "gingivitis": "gingivitis",
      "enfermedad periodontal": "enfermedadPeriodontal",
      "halitosis": "halitosis",
      "movilidad dental": "movilidadDental",
      "dificultad para masticar": "dificultadMasticar",
      "dificultad masticar": "dificultadMasticar",

      "seguro médico": "seguroMedico",
      "seguro medico": "seguroMedico",
      "número de póliza": "numeroPoliza",
      "numero de poliza": "numeroPoliza",
      "contacto de emergencia": "contactoEmergencia",
      "teléfono de emergencia": "telefonoEmergencia",
      "telefono de emergencia": "telefonoEmergencia",
      "parentesco de emergencia": "parentescoEmergencia",
      "observaciones": "observaciones",
    };

    const campoPorTexto = equivalenciasTexto[texto];

    if (campoPorTexto) {
      return {
        ...pregunta,
        campo: campoPorTexto,
      };
    }

    return pregunta;
  };

  // ============================================================
  // SI YA ES VERSIÓN 2:
  // CONSERVAR LAS PREGUNTAS CONFIGURADAS POR LA CLÍNICA,
  // PERO COMPLETAR LOS CAMPOS LEGACY QUE FALTEN.
  // ============================================================
  if (
    data.version >= 2 &&
    Array.isArray(data.secciones)
  ) {
    const seccionesBase = base.secciones;

    const seccionesExistentes = data.secciones.map((seccion) => ({
      ...seccion,
      preguntas: Array.isArray(seccion.preguntas)
        ? seccion.preguntas.map(completarPregunta)
        : [],
    }));

    const seccionesFinales = seccionesBase.map((seccionBase) => {
      const existente = seccionesExistentes.find(
        (seccion) => seccion.id === seccionBase.id
      );

      if (!existente) {
        return seccionBase;
      }

      return {
        ...seccionBase,
        ...existente,
        preguntas: existente.preguntas,
      };
    });

    return {
      ...base,
      ...data,
      version: 2,
      secciones: seccionesFinales,
    };
  }

  // ============================================================
  // MIGRACIÓN DE CONFIGURACIONES ANTERIORES
  // ============================================================
  const seccionesMigradas = base.secciones.map((seccionBase) => {
    let preguntasEncontradas = [];

    const idsOrigen = {
      general: [
        "datos_generales",
        "general",
      ],
      direccion: [
        "direccion",
      ],
      clinico: [
        "clinico",
        "habitos",
        "higiene_oral",
        "dolor",
        "motivo_consulta",
      ],
      extras: [
        "observaciones",
        "extras",
      ],
    };

    const origenes = idsOrigen[seccionBase.id] || [];

    origenes.forEach((idOrigen) => {
      const seccionOrigen = Array.isArray(data.secciones)
        ? data.secciones.find(
            (seccion) => seccion.id === idOrigen
          )
        : null;

      if (
        seccionOrigen &&
        Array.isArray(seccionOrigen.preguntas)
      ) {
        preguntasEncontradas.push(
          ...seccionOrigen.preguntas
        );
      }
    });

    // Eliminar duplicados por ID.
    const preguntasUnicas = [];
    const idsVistos = new Set();

    preguntasEncontradas.forEach((pregunta) => {
      if (!pregunta || !pregunta.id) return;

      if (idsVistos.has(pregunta.id)) return;

      idsVistos.add(pregunta.id);
      preguntasUnicas.push(
        completarPregunta(pregunta)
      );
    });

    // Mantener las preguntas base que ya existan,
    // respetando la configuración guardada.
    const preguntasFinales = seccionBase.preguntas.map(
      (preguntaBase) => {
        const existente = preguntasUnicas.find(
          (pregunta) => pregunta.id === preguntaBase.id
        );

        if (!existente) {
          return preguntaBase;
        }

        return {
          ...preguntaBase,
          ...existente,
          campo:
            existente.campo ||
            preguntaBase.campo ||
            undefined,
        };
      }
    );

    // Agregar preguntas personalizadas que no existan
    // en la configuración base.
    preguntasUnicas.forEach((pregunta) => {
      const yaExiste = preguntasFinales.some(
        (existente) => existente.id === pregunta.id
      );

      if (!yaExiste) {
        preguntasFinales.push(pregunta);
      }
    });

    return {
      ...seccionBase,
      preguntas: preguntasFinales,
    };
  });

  return {
    ...base,
    ...data,
    version: 2,
    secciones: seccionesMigradas,
  };
}

/*
=========================================================
 COMPONENTE PRINCIPAL
=========================================================
*/

export default function HistoriaClinicaSettings({

  onRegisterSave,

  onSavingChange

}) {


  const {
    clinicId
  } = useClinic();


  const [
    configuracion,
    setConfiguracion
  ] = useState(
    HISTORIA_CLINICA_DEFAULT
  );


  const [
    loading,
    setLoading
  ] = useState(true);


  const [
    saving,
    setSaving
  ] = useState(false);


  const [
    mensaje,
    setMensaje
  ] = useState("");


  const [
    seccionAbierta,
    setSeccionAbierta
  ] = useState(null);


  /*
  ========================================================
   CARGAR CONFIGURACIÓN
  ========================================================
  */

  useEffect(() => {

    if (!clinicId) {

      setLoading(false);

      return;

    }


    const cargar = async () => {

      try {

        setLoading(true);


        const ref = doc(

          db,

          "clinics",

          clinicId,

          "historiaClinicaConfig",

          "config"

        );


        const snapshot =
          await getDoc(ref);


        if (
          snapshot.exists()
        ) {

          const data =
            snapshot.data();


          if (
            data &&
            Array.isArray(
              data.secciones
            )
          ) {

            setConfiguracion(
              normalizarConfiguracionHistoria(
                data
              )
            );


            console.log(
              "HISTORIA CLÍNICA: configuración cargada",
              data
            );


            return;

          }

        }


        /*
        ==================================================
         CONSULTORIO SIN CONFIGURACIÓN
        ==================================================

         Usamos la configuración predeterminada.

         Todavía NO escribimos nada en Firebase.
        */

        setConfiguracion(
          HISTORIA_CLINICA_DEFAULT
        );


        console.log(
          "HISTORIA CLÍNICA: usando configuración predeterminada"
        );


      } catch (err) {

        console.error(
          "Error cargando historia clínica:",
          err
        );


        /*
        ==================================================
         SI HAY ERROR DE LECTURA
        ==================================================
        */

        setConfiguracion(
          HISTORIA_CLINICA_DEFAULT
        );

      } finally {

        setLoading(false);

      }

    };


    cargar();

  }, [clinicId]);


  /*
  ========================================================
   AGREGAR SECCIÓN
  ========================================================
  */

  const agregarSeccion = () => {

    const id =
      `seccion_${Date.now()}`;


    const nueva = {

      id,

      titulo:
        "Nueva sección",

      preguntas: []

    };


    setConfiguracion(
      prev => ({

        ...prev,

        secciones: [

          ...prev.secciones,

          nueva

        ]

      })
    );


    setSeccionAbierta(id);

  };


  /*
  ========================================================
   ELIMINAR SECCIÓN
  ========================================================
  */

  const eliminarSeccion = async (
    seccionId
  ) => {

    const ok =
      await requestVexaConfirm(
        "¿Eliminar esta sección y todas sus preguntas?"
      );


    if (!ok) return;


    setConfiguracion(
      prev => ({

        ...prev,

        secciones:
          prev.secciones.filter(
            s =>
              s.id !== seccionId
          )

      })
    );

  };


  /*
  ========================================================
   CAMBIAR TÍTULO
  ========================================================
  */

  const cambiarTituloSeccion = (
    seccionId,
    valor
  ) => {

    setConfiguracion(
      prev => ({

        ...prev,

        secciones:
          prev.secciones.map(
            s =>

              s.id === seccionId

                ? {
                    ...s,
                    titulo: valor
                  }

                : s

          )

      })
    );

  };


  /*
  ========================================================
   AGREGAR PREGUNTA
  ========================================================
  */

  const agregarPregunta = (
    seccionId
  ) => {

    const id =
      `pregunta_${Date.now()}`;


    const nuevaPregunta = {

      id,

      texto:
        "Nueva pregunta",

      tipo:
        "texto",

      obligatorio:
        false,

      permitirEspecificar:
        false,

      especificacionLabel:
        "Especifique",

      opciones:
        [],

      generaAlerta:
        false,

      alertaTexto:
        ""

    };


    setConfiguracion(
      prev => ({

        ...prev,

        secciones:
          prev.secciones.map(
            s =>

              s.id === seccionId

                ? {

                    ...s,

                    preguntas: [

                      ...s.preguntas,

                      nuevaPregunta

                    ]

                  }

                : s

          )

      })
    );

  };


  /*
  ========================================================
   ELIMINAR PREGUNTA
  ========================================================
  */

  const eliminarPregunta = async (
    seccionId,
    preguntaId
  ) => {

    const ok =
      await requestVexaConfirm(
        "¿Eliminar esta pregunta?"
      );


    if (!ok) return;


    setConfiguracion(
      prev => ({

        ...prev,

        secciones:
          prev.secciones.map(
            s =>

              s.id === seccionId

                ? {

                    ...s,

                    preguntas:
                      s.preguntas.filter(
                        pregunta =>
                          pregunta.id !==
                          preguntaId
                      )

                  }

                : s

          )

      })
    );

  };


  /*
  ========================================================
   ACTUALIZAR PREGUNTA
  ========================================================
  */

  const actualizarPregunta = (
    seccionId,
    preguntaId,
    campo,
    valor
  ) => {

    setConfiguracion(
      prev => ({

        ...prev,

        secciones:
          prev.secciones.map(
            s =>

              s.id === seccionId

                ? {

                    ...s,

                    preguntas:
                      s.preguntas.map(
                        pregunta =>

                          pregunta.id ===
                          preguntaId

                            ? {
                                ...pregunta,
                                [campo]:
                                  valor
                              }

                            : pregunta

                      )

                  }

                : s

          )

      })
    );

  };


  /*
  ========================================================
   GUARDAR
  ========================================================
  */

  const guardar = async () => {

    if (!clinicId) {

      alert(
        "No se encontró el consultorio."
      );

      return;

    }


    try {

      setSaving(true);


      if (onSavingChange) {

        onSavingChange(true);

      }


      setMensaje("");


      const configuracionFinal =
        normalizarConfiguracionHistoria({

          ...configuracion,

          version: 2

        });


      await setDoc(

        doc(

          db,

          "clinics",

          clinicId,

          "historiaClinicaConfig",

          "config"

        ),

        {

          ...configuracionFinal,

          updatedAt:
            serverTimestamp()

        },

        {

          merge: true

        }

      );


      /*
      ====================================================
       IMPORTANTE

       La configuración se guarda dentro del
       clinicId actual.

       Por lo tanto:

       Dentania
       ↓
       clinics/dentania/historiaClinicaConfig/config

       Otro consultorio
       ↓
       clinics/otroConsultorio/historiaClinicaConfig/config

       NO comparten configuración.
      ====================================================
      */


      setMensaje(
        "Historia clínica guardada correctamente."
      );


      setTimeout(() => {

        setMensaje("");

      }, 3000);


    } catch (err) {

      console.error(
        "Error guardando historia clínica:",
        err
      );


      setMensaje(

        `Error al guardar: ${
          err.code ||
          err.message ||
          "Error desconocido"
        }`

      );

    } finally {

      setSaving(false);


      if (onSavingChange) {

        onSavingChange(false);

      }

    }

  };


  /*
  ========================================================
   REGISTRAR GUARDADO EN SETTINGS PANEL
  ========================================================
  */

  useEffect(() => {

    if (!onRegisterSave) {

      return;

    }


    /*
    IMPORTANTE:

    Se necesita doble función porque el setter
    de React interpreta una función como updater.

    Queremos registrar:

      () => guardar()

    y no ejecutar guardar inmediatamente.
    */

    onRegisterSave(
      () => () => guardar()
    );


    return () => {

      onRegisterSave(null);

    };

  }, [

    onRegisterSave,

    configuracion,

    clinicId

  ]);

  const totalPreguntas = configuracion.secciones.reduce(
    (total, seccion) => total + seccion.preguntas.length,
    0
  );

  const preguntasObligatorias = configuracion.secciones.reduce(
    (total, seccion) => total + seccion.preguntas.filter(pregunta => pregunta.obligatorio).length,
    0
  );

  const preguntasConAlerta = configuracion.secciones.reduce(
    (total, seccion) => total + seccion.preguntas.filter(pregunta => pregunta.generaAlerta).length,
    0
  );


  /*
  ========================================================
   LOADING
  ========================================================
  */

  if (loading) {

    return (

      <div style={styles.loading}>

        Cargando historia clínica...

      </div>

    );

  }


  /*
  ========================================================
   RENDER
  ========================================================
  */

  return (

    <div className="vd-settings-module vd-settings-history" style={styles.container}>

      <div style={styles.header}>

        <div>

          <h2 style={styles.title}>

            Historia clínica

          </h2>


          <p style={styles.subtitle}>

            Personaliza las preguntas que forman
            parte del expediente clínico de tu
            consultorio.

          </p>

        </div>

      </div>

      <div className="vd-settings-module-stats">
        <div><strong>{configuracion.secciones.length}</strong><span>secciones configuradas</span></div>
        <div><strong>{totalPreguntas}</strong><span>preguntas en el expediente</span></div>
        <div><strong>{preguntasObligatorias}</strong><span>campos obligatorios</span></div>
        <div><strong>{preguntasConAlerta}</strong><span>alertas clínicas</span></div>
      </div>


      {mensaje && (

        <div style={styles.message}>

          {mensaje}

        </div>

      )}


      <div style={styles.warning}>

        Los cambios realizados aquí definirán
        las preguntas disponibles en la historia
        clínica del consultorio.

      </div>


      <div style={styles.sections}>

        {configuracion.secciones.map(

          seccion => (

            <div

              key={seccion.id}

              style={styles.section}

            >

              <div
                style={
                  styles.sectionHeader
                }
              >

                <div
                  style={
                    styles.sectionTitleArea
                  }
                >

                  <GripVertical
                    size={18}
                    color="#9ca3af"
                  />


                  <div
                    style={{
                      fontSize: 17,
                      fontWeight: 800,
                      color: "#111827"
                    }}
                  >

                    {seccion.titulo}

                  </div>

                </div>


                <div
                  style={
                    styles.sectionActions
                  }
                >

                  <button

                    onClick={() =>
                      setSeccionAbierta(

                        seccionAbierta ===
                        seccion.id

                          ? null

                          : seccion.id

                      )
                    }

                    style={
                      styles.iconButton
                    }

                    title="Mostrar / ocultar"

                  >

                    {seccionAbierta ===
                    seccion.id

                      ? (

                          <ChevronUp
                            size={18}
                          />

                        )

                      : (

                          <ChevronDown
                            size={18}
                          />

                        )

                    }

                  </button>

                </div>

              </div>


              {seccionAbierta ===
                seccion.id && (

                <div
                  style={
                    styles.questions
                  }
                >

                  {seccion.preguntas.map(

                    pregunta => (

                      <PreguntaEditor

                        key={
                          pregunta.id
                        }

                        pregunta={
                          pregunta
                        }

                        onChange={(
                          campo,
                          valor
                        ) =>

                          actualizarPregunta(

                            seccion.id,

                            pregunta.id,

                            campo,

                            valor

                          )

                        }

                        onDelete={() =>

                          eliminarPregunta(

                            seccion.id,

                            pregunta.id

                          )

                        }

                      />

                    )

                  )}


                  <button

                    onClick={() =>
                      agregarPregunta(
                        seccion.id
                      )
                    }

                    style={
                      styles.addQuestionButton
                    }

                  >

                    <Plus
                      size={17}
                    />

                    Agregar pregunta

                  </button>

                </div>

              )}

            </div>

          )

        )}

      </div>

    </div>

  );

}


/*
=========================================================
 EDITOR DE PREGUNTA
=========================================================
*/

function PreguntaEditor({

  pregunta,

  onChange,

  onDelete

}) {

  return (

    <div style={styles.question}>

      <div
        style={styles.questionTop}
      >

        <div
          style={styles.questionNumber}
        >

          Pregunta

        </div>


        <button

          onClick={onDelete}

          style={
            styles.questionDelete
          }

          title="Eliminar pregunta"

        >

          <Trash2
            size={16}
          />

          Eliminar

        </button>

      </div>


      <div style={styles.field}>

        <label
          style={styles.label}
        >

          Texto de la pregunta

        </label>


        <input

          value={
            pregunta.texto || ""
          }

          onChange={e =>
            onChange(
              "texto",
              e.target.value
            )
          }

          style={styles.input}

        />

      </div>


      <div
        style={styles.twoColumns}
      >

        <div style={styles.field}>

          <label
            style={styles.label}
          >

            Tipo de respuesta

          </label>


          <select

            value={
              pregunta.tipo ||
              "texto"
            }

            onChange={e =>
              onChange(
                "tipo",
                e.target.value
              )
            }

            style={styles.input}

          >

            <option value="texto">

              Campo abierto

            </option>


            <option value="texto_largo">

              Campo abierto largo

            </option>


            <option value="si_no">

              Sí / No

            </option>


            <option value="checkbox">

              Checkbox

            </option>


            <option value="seleccion">

              Selección única

            </option>


            <option value="seleccion_multiple">

              Selección múltiple

            </option>


            <option value="numero">

              Número

            </option>


            <option value="fecha">

              Fecha

            </option>

            <option value="telefono">

              Teléfono

            </option>

          </select>

        </div>


        <div style={styles.field}>

          <label
            style={styles.label}
          >

            Obligatoria

          </label>


          <label
            style={styles.switchRow}
          >

            <input

              type="checkbox"

              checked={
                !!pregunta.obligatorio
              }

              onChange={e =>
                onChange(
                  "obligatorio",
                  e.target.checked
                )
              }

            />


            <span>

              Respuesta Obligatoria

            </span>

          </label>

        </div>

      </div>


      {(
        pregunta.tipo ===
          "checkbox" ||

        pregunta.tipo ===
          "si_no"

      ) && (

        <div
          style={
            styles.optionBox
          }
        >

          <label
            style={
              styles.switchRow
            }
          >

            <input

              type="checkbox"

              checked={
                !!pregunta.permitirEspecificar
              }

              onChange={e =>
                onChange(
                  "permitirEspecificar",
                  e.target.checked
                )
              }

            />


            <span>

              Permitir especificar

            </span>

          </label>


          {pregunta.permitirEspecificar && (

            <input

              value={
                pregunta
                  .especificacionLabel ||
                "Especifique"
              }

              onChange={e =>
                onChange(
                  "especificacionLabel",
                  e.target.value
                )
              }

              placeholder={
                "Texto del campo de especificación"
              }

              style={styles.input}

            />

          )}

        </div>

      )}


      {(
        pregunta.tipo ===
          "seleccion" ||

        pregunta.tipo ===
          "seleccion_multiple"

      ) && (

        <div
          style={
            styles.optionBox
          }
        >

          <label
            style={styles.label}
          >

            Opciones

          </label>


          <textarea

            value={(
              pregunta.opciones ||
              []
            ).join("\n")}

            onChange={e => {

              const opciones =
                e.target.value
                  .split("\n")
                  .map(
                    x =>
                      x.trim()
                  )
                  .filter(Boolean);


              onChange(
                "opciones",
                opciones
              );

            }}

            placeholder={
              "Una opción por línea"
            }

            style={
              styles.textarea
            }

          />

        </div>

      )}


      <div
        style={styles.alertBox}
      >

        <label
          style={styles.switchRow}
        >

          <input

            type="checkbox"

            checked={
              !!pregunta.generaAlerta
            }

            onChange={e =>
              onChange(
                "generaAlerta",
                e.target.checked
              )
            }

          />


          <span>

            Generar alerta clínica

          </span>

        </label>


        {pregunta.generaAlerta && (

          <input

            value={
              pregunta.alertaTexto ||
              ""
            }

            onChange={e =>
              onChange(
                "alertaTexto",
                e.target.value
              )
            }

            placeholder={
              "Texto que aparecerá como alerta"
            }

            style={styles.input}

          />

        )}

      </div>

    </div>

  );

}


/*
=========================================================
 ESTILOS
=========================================================
*/

const styles = {

  container: {

    width: "100%",

    maxWidth: 1100,

    margin: "0 auto",

    padding: 24,

    boxSizing: "border-box"

  },


  loading: {

    padding: 40,

    textAlign: "center",

    color: "#6b7280"

  },


  header: {

    display: "flex",

    justifyContent:
      "space-between",

    alignItems:
      "flex-start",

    gap: 20,

    marginBottom: 20,

    flexWrap: "wrap"

  },


  title: {

    margin: 0,

    fontSize: 28,

    fontWeight: 800,

    color: "#111827"

  },


  subtitle: {

    margin: "6px 0 0",

    color: "#6b7280",

    fontSize: 14,

    lineHeight: 1.5

  },


  message: {

    padding: "12px 16px",

    borderRadius: 12,

    background: "#dcfce7",

    color: "#166534",

    marginBottom: 16,

    fontSize: 14,

    fontWeight: 600

  },


  warning: {

    padding: "14px 16px",

    borderRadius: 14,

    background: "#eff6ff",

    border:
      "1px solid #bfdbfe",

    color: "#1e40af",

    marginBottom: 20,

    fontSize: 13,

    lineHeight: 1.5

  },


  sections: {

    display: "flex",

    flexDirection:
      "column",

    gap: 14

  },


  section: {

    background: "#fff",

    border:
      "1px solid #e5e7eb",

    borderRadius: 18,

    overflow: "hidden",

    boxShadow:
      "0 4px 14px rgba(15,23,42,.05)"

  },


  sectionHeader: {

    display: "flex",

    justifyContent:
      "space-between",

    alignItems: "center",

    padding: "14px 16px",

    background: "#f8fafc",

    borderBottom:
      "1px solid #e5e7eb"

  },


  sectionTitleArea: {

    display: "flex",

    alignItems: "center",

    gap: 8,

    flex: 1

  },


  sectionActions: {

    display: "flex",

    gap: 6

  },


  iconButton: {

    width: 36,

    height: 36,

    border: "none",

    borderRadius: 10,

    background: "#e5e7eb",

    color: "#374151",

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    cursor: "pointer"

  },


  questions: {

    padding: 16,

    display: "flex",

    flexDirection:
      "column",

    gap: 14

  },


  question: {

    border:
      "1px solid #e5e7eb",

    borderRadius: 14,

    padding: 16,

    background: "#fff"

  },


  questionTop: {

    display: "flex",

    justifyContent:
      "space-between",

    alignItems: "center",

    marginBottom: 14

  },


  questionNumber: {

    fontSize: 12,

    fontWeight: 800,

    color: "#6b7280",

    textTransform:
      "uppercase"

  },


  questionDelete: {

    display: "inline-flex",

    alignItems: "center",

    gap: 6,

    border: "none",

    borderRadius: 9,

    background: "#fee2e2",

    color: "#b91c1c",

    padding: "7px 10px",

    cursor: "pointer",

    fontWeight: 600

  },


  field: {

    display: "flex",

    flexDirection:
      "column",

    gap: 6,

    marginBottom: 14

  },


  label: {

    fontSize: 12,

    fontWeight: 700,

    color: "#374151"

  },


  input: {

    width: "100%",

    boxSizing:
      "border-box",

    padding:
      "10px 12px",

    borderRadius: 10,

    border:
      "1px solid #d1d5db",

    fontSize: 13,

    outline: "none",

    background: "#fff"

  },


  textarea: {

    width: "100%",

    minHeight: 90,

    boxSizing:
      "border-box",

    padding:
      "10px 12px",

    borderRadius: 10,

    border:
      "1px solid #d1d5db",

    fontSize: 13,

    outline: "none",

    resize: "vertical"

  },


  twoColumns: {

    display: "grid",

    gridTemplateColumns:
      "minmax(0,1fr) minmax(0,1fr)",

    gap: 14

  },


  switchRow: {

    display: "flex",

    alignItems: "center",

    gap: 8,

    fontSize: 13,

    color: "#374151",

    cursor: "pointer"

  },


  optionBox: {

    padding: 12,

    borderRadius: 12,

    background: "#f8fafc",

    border:
      "1px solid #e5e7eb",

    marginBottom: 14

  },


  alertBox: {

    padding: 12,

    borderRadius: 12,

    background: "#fff7ed",

    border:
      "1px solid #fed7aa"

  },


  addQuestionButton: {

    display: "inline-flex",

    alignItems: "center",

    justifyContent:
      "center",

    gap: 8,

    padding:
      "11px 14px",

    borderRadius: 11,

    border:
      "1px dashed #93c5fd",

    background: "#eff6ff",

    color: "#1d4ed8",

    cursor: "pointer",

    fontWeight: 700

  }

};
